import React from 'react';
import { isNativeApp } from '../utils/nativePushBridge.js';

/**
 * Modal component to inform users that the service hasn't launched yet
 */
export const PilotUserModal = ({ isOpen, onClose }) => {
  const isInNativeApp = isNativeApp();
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-3 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-4 sm:p-6 md:p-8 relative animate-fadeIn my-4">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 left-3 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="إغلاق"
        >
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Icon */}
        <div className="text-center mb-4 sm:mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full mb-3">
            <span className="text-4xl sm:text-5xl">🚀</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 px-2">
            قريباً في مدينتك!
          </h2>
        </div>

        {/* Content */}
        <div className="text-center space-y-3 sm:space-y-4 mb-6 sm:mb-8">
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed px-2">
            صلحلي تعمل على بناء شبكة كبيرة من مقدمي الخدمات في مدينتك
          </p>
          
          {/* Launch Timeline - Made More Prominent */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-4 sm:p-6 mt-3 sm:mt-4">
            <div className="flex items-center justify-center mb-2 sm:mb-3">
              <span className="text-2xl sm:text-3xl ml-2">📅</span>
              <p className="text-base sm:text-xl font-bold text-blue-900">
                موعد الإنطلاق المتوقع
              </p>
            </div>
            <p className="text-xl sm:text-2xl font-extrabold text-blue-600 mb-1 sm:mb-2">
              بداية العام الجديد 2026
            </p>
            <p className="text-sm sm:text-base text-gray-700">
              (أو قبل ذلك إن شاء الله)
            </p>
          </div>
          
          {!isInNativeApp && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mt-4 sm:mt-6">
              <p className="text-blue-800 font-medium text-sm sm:text-base">
                📱 حمّل التطبيق الآن
              </p>
              <p className="text-blue-700 text-xs sm:text-sm mt-2">
                احصل على إشعار فوري عندما تكون شبكة مقدمي الخدمات جاهزة لاستقبال طلباتك
              </p>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="space-y-2 sm:space-y-3">
          {!isInNativeApp && (
            <>
              <button
                onClick={() => window.open('https://play.google.com/store/apps/details?id=com.salehli.app', '_blank')}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                تحميل من غوغل بلاي
              </button>
              <button
                onClick={() => window.open('https://apps.apple.com/app/salehli/id123456789', '_blank')}
                className="w-full bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:from-gray-900 hover:to-black transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                تحميل من متجر أبل
              </button>
            </>
          )}
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-medium hover:bg-gray-200 transition-colors"
          >
            تصفح الخدمات
          </button>
        </div>
      </div>
    </div>
  );
};
